import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { Amount } from '@fcom/dapi/interfaces/dapi.interface';
import { MediaQueryService } from '@fcom/common/services/media-query/media-query.service';
import { stopPropagation } from '@fcom/core/utils/event';

import { ButtonMode, ButtonTheme } from './../../buttons/button/enums';
import { IconPosition } from '../../icons/enums';
import { ResponsiveImage } from '../../images/interfaces';
import { NotificationLayout, NotificationTheme } from '../../notifications/enums';
import { ProductTag } from '../../../../common-booking/modules/ancillaries/interfaces/ancillaries-interface';

@Component({
  selector: 'fcom-product-card',
  templateUrl: './product-card.component.html',
  styleUrls: ['./product-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductCardComponent {
  readonly ButtonMode = ButtonMode;
  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationLayout = NotificationLayout;

  private mediaQueryService = inject(MediaQueryService);

  hasAddedItems = input<boolean>();

  /**
   * The title of the product.
   */
  title = input<string>();

  /**
   * The detailed description of the product.
   */
  detailText = input<string>();

  /**
   * Control description visibility.
   */
  showDetailText = input(false);

  /**
   * The image of the product.
   */
  image = input<ResponsiveImage>();

  /**
   * The current status text to be shown for the product.
   * Should be used to to show for example how many of this product we have selected
   */
  statusText = input<string>();

  /**
   * The purchased text to be shown for the product.
   * Should be used to to show how many of this product have previously been bought
   */
  purchasedText = input<string>();

  /**
   * Price of the product
   */
  price = input<string>();

  /**
   * Primary button loading state for the product
   */
  loading = input<boolean>();

  /**
   * Primary button theme
   */
  buttonTheme = input<ButtonTheme>(ButtonTheme.TERTIARY_GHOST);

  /**
   * Primary button text
   */
  buttonText = input<string>();

  /**
   * Primary button aria text
   */
  buttonAriaText = input<string>();

  /**
   * If button should be disabled for the product
   */
  hideButton = input(false);

  /**
   * Primary button icon
   */
  buttonIcon = input<SvgLibraryIcon>(SvgLibraryIcon.PLUS);

  /**
   * Primary button icon position
   */
  buttonIconPosition = input<IconPosition>(IconPosition.RIGHT);

  /**
   * Notification translated text
   */
  notificationText = input<string>();

  /**
   * Notification translated title
   */
  notificationTitle = input<string>();

  /**
   * Notification icon
   */
  notificationIcon = input<SvgLibraryIcon>();

  /**
   * Notification theme
   */
  notificationTheme = input<NotificationTheme>(NotificationTheme.INFO);

  focusOnBtn = input(false);

  lowestPrice = input<string>();

  originalPrice = input<string>();

  productTags = input<ProductTag[]>([]);

  lowQuota = input<{
    count: number;
    segment: string;
  }>();

  useNewProductCardLayout = input(false);

  countText = input<string>();

  aviosPrice = input<Amount | null>();

  aviosLowestPrice = input<string>();

  /**
   * Event emitter for selectProduct event from primary button
   */
  selectProduct = output<void>();

  protected readonly isLaptopUp = this.mediaQueryService.isBreakpoint('laptop_up');

  emitProductCategory(event: Event): void {
    stopPropagation(event);
    this.selectProduct.emit();
  }
}
